import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import CanvasEditor from './components/CanvasEditor';
import ReferenceImage from './components/ReferenceImage';
import Sidebar from './components/Sidebar';
import RightSidebar from './components/RightSidebar';
import AuthModal from './components/AuthModal';

// API configuration
// Detect if running in development (localhost) or production
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (isDevelopment ? 'http://localhost:4001' : 'https://charmupdateapi.cs2inspects.com');
const CS2INSPECTS_API_BASE_URL = process.env.CS2INSPECTS_API_BASE_URL || 'https://api.cs2inspects.com';

interface ApiData {
  Item_ID?: string;
  Skin_ID?: string;
  Item_Name?: string;
  KeyChain_ID?: string;
  KeyChain_Name?: string;
  KeyChain_Pattern?: string;
  KeyChain_X?: string;
  KeyChain_Y?: string;
  KeyChain_Z?: string;
  KeyChain_Image_Front?: string;
  Item_Model_isLegacy?: boolean; // Added for the new save handler
}

interface KnownPlacement {
  id?: string;
  _id?: { $oid: string };
  charm_id: string;
  charm_name: string;
  posx: number;
  posy: number;
  x: number;
  y: number;
  z: number;
  islegacy: boolean;
  isactive: boolean;
  inspectlink?: string; // Added for loading inspectlink
  charmlink_image?: string; // Added for loading the actual picture
}

interface Charmlink {
  _id: { $oid: string };
  def_index: number;
  paint_index: number;
  asset_id: { $numberLong: string };
  offset_x: number;
  offset_y: number;
  offset_z: number;
  slot: number;
  sticker_id: number;
  keychain: any;
  S: { $numberLong: string };
  A: { $numberLong: string };
  D: string;
  M: any;
  inspectlink: string;
  inspectlinkconsole?: string;
  image?: string;
  islegacy?: boolean;
  isactive?: boolean;
  isskip?: boolean;
  isloaded?: boolean;
}

const weapon_defindex_map = {
    'AK-47': 7,
    'M4A1-S': 60,
    'M4A4': 16,
    'AWP': 9,
    'SSG 08': 40,
    'Galil AR': 13,
    'FAMAS': 10,
    'AUG': 8,
    'SG 553': 39,
    'G3SG1': 11,
    'SCAR-20': 38,
    'Glock-18': 4,
    'USP-S': 61,
    'P2000': 32,
    'P250': 36,
    'Dual Berettas': 2,
    'Tec-9': 30,
    'Five-SeveN': 3,
    'CZ75-Auto': 63,
    'R8 Revolver': 64,
    'Desert Eagle': 1,
    'MAC-10': 17,
    'MP9': 34,
    'UMP-45': 24,
    'MP7': 33,
    'MP5-SD': 23,
    'PP-Bizon': 26,
    'P90': 19,
    'Nova': 35,
    'MAG-7': 27,
    'XM1014': 25,
    'Sawed-Off': 29,
    'Negev': 28,
    'M249': 14,
    'Zeus x27': 31,
};





function App() {
  const [imagePosition, setImagePosition] = useState({ x: 960, y: 540 }); // Center of 1920x1080 grid
  const [inspectLink, setInspectLink] = useState('');
  const [apiData, setApiData] = useState<ApiData>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [statusType, setStatusType] = useState<'success' | 'error' | 'info'>('info');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<string>('');
  const [authError, setAuthError] = useState<string>('');
  const [referenceImageUrl, setReferenceImageUrl] = useState<string>('https://screenshots.inspects.xyz/d8be940b-cab2-4e36-8089-5076cc814cbd-front.webp');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string>('https://cdn.cs2inspects.com/customizer/7_44_0.webp');
  const [selectedWeapon, setSelectedWeapon] = useState<string>('');
  const [knownPlacements, setKnownPlacements] = useState<KnownPlacement[]>([]);
  const [currentPlacementId, setCurrentPlacementId] = useState<string | undefined>(undefined);
  const [charmScale, setCharmScale] = useState(1.0);
  const [newPlacements, setNewPlacements] = useState<Charmlink[]>([]);
  const [imageReadyCount, setImageReadyCount] = useState<number>(0);
  const [totalAllCount, setTotalAllCount] = useState<number>(0);
  const [currentCharmlink, setCurrentCharmlink] = useState<Charmlink | null>(null);
  const [weaponCounts, setWeaponCounts] = useState<Record<string, { existing: { new: number; legacy: number }; available: { new: number; legacy: number } }>>({});
  const [scale, setScale] = useState(0.8);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });

  // Check authentication status on component mount
  useEffect(() => {
    // Handle Steam authentication callback first
    handleSteamCallback();
    checkAuthStatus();
  }, []);

  // Check if user is already authenticated
  const checkAuthStatus = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setIsAuthenticated(true);
        setCurrentUser(data.user.steamId);
        fetchWeaponCounts();
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      console.error('Auth verification error:', error);
      localStorage.removeItem('authToken');
    }
  };

  // Debug button states
  useEffect(() => {
    console.log('Button states:', {
      hasCharmlink: !!currentCharmlink,
      isSkipped: currentCharmlink?.isskip,
      skipDisabled: !currentCharmlink || currentCharmlink?.isskip,
      nextDisabled: newPlacements.length === 0,
      saveAndNextDisabled: newPlacements.length === 0,
      deleteDisabled: !currentPlacementId,
      hasApiData: !!apiData.Item_ID,
      hasInspectLink: !!inspectLink
    });
  }, [currentCharmlink, newPlacements.length, currentPlacementId, apiData.Item_ID, inspectLink]);
  const [showDots, setShowDots] = useState(true);
  const [useActualImage, setUseActualImage] = useState(true);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [fetchingCharmlinks, setFetchingCharmlinks] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Function to reset to main home state
  const resetToHomeState = () => {
    setInspectLink('');
    setApiData({});
    setSelectedWeapon('');
    setKnownPlacements([]);
    setCurrentPlacementId(undefined);
    setNewPlacements([]);
    setImageReadyCount(0);
    setTotalAllCount(0);
    setCurrentCharmlink(null);
    // Don't reset weaponCounts - these are global counts that should persist
    setError('');
    setStatusMessage('');
    setStatusType('info');
    setImageGenerating(false);
    setFetchingCharmlinks(false);
    setIsProcessing(false);
    // Reset to default reference image
    setReferenceImageUrl('https://screenshots.inspects.xyz/d8be940b-cab2-4e36-8089-5076cc814cbd-front.webp');
    setBackgroundImageUrl('https://cdn.cs2inspects.com/customizer/7_44_0.webp');
  };

  const handleSteamLogin = () => {
    // Redirect to Steam authentication
    console.log('🚀 [STEAM AUTH] Initiating Steam login...');
    console.log('🚀 [STEAM AUTH] Redirecting to:', `${API_BASE_URL}/api/auth/steam`);
    window.location.href = `${API_BASE_URL}/api/auth/steam`;
  };

  // Handle Steam authentication callback
  const handleSteamCallback = () => {
    console.log('🔄 [STEAM AUTH] Checking for Steam callback...');
    console.log('🔄 [STEAM AUTH] Current URL:', window.location.href);
    
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const steamId = urlParams.get('steamId');
    const steamUsername = urlParams.get('steamUsername');
    const error = urlParams.get('error');
    const message = urlParams.get('message');
    
    console.log('🔄 [STEAM AUTH] URL Parameters:', {
      token: token ? 'Present' : 'Missing',
      steamId: steamId || 'Missing',
      steamUsername: steamUsername || 'Missing',
      error: error || 'None',
      message: message || 'None'
    });

    if (error) {
      console.log('❌ [STEAM AUTH] Authentication failed:', error, message);
      setAuthError(message || 'Authentication failed');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (token && steamId && steamUsername) {
      console.log('✅ [STEAM AUTH] Authentication successful!');
      console.log('✅ [STEAM AUTH] User:', steamUsername, '(' + steamId + ')');
      console.log('✅ [STEAM AUTH] Token received:', token.substring(0, 20) + '...');
      
      // Store the JWT token in localStorage
      localStorage.setItem('authToken', token);
      setIsAuthenticated(true);
      setCurrentUser(steamId);
      setAuthError('');
      fetchWeaponCounts();
      
      console.log('✅ [STEAM AUTH] User authenticated, fetching weapon counts...');
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      console.log('ℹ️ [STEAM AUTH] No authentication parameters found, user not logged in');
    }
  };

  const handleInspectLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInspectLink(e.target.value);
    // Clear current placement ID when user enters a new inspect link
    // This ensures it's treated as a new placement, not an update
    setCurrentPlacementId(undefined);
    
    // Clear current charmlink and image when user types new inspect link
    setCurrentCharmlink(null);
    setReferenceImageUrl('https://screenshots.inspects.xyz/d8be940b-cab2-4e36-8089-5076cc814cbd-front.webp');
  };

  const handleWeaponSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    setSelectedWeapon(selectedValue);
    
    if (selectedValue) {
      // Parse value like "AK-47-new" or "AK-47-legacy"
      const lastDash = selectedValue.lastIndexOf('-');
      const weaponName = selectedValue.substring(0, lastDash);
      const legacyType = selectedValue.substring(lastDash + 1);
      const item_id = weapon_defindex_map[weaponName as keyof typeof weapon_defindex_map];
      const isLegacy = legacyType === 'legacy';
      if (item_id) {
        fetchKnownPlacements(item_id, isLegacy);
        fetchCharmlinks(item_id, isLegacy);
      }
    } else {
      setKnownPlacements([]);
      setNewPlacements([]);
      setImageReadyCount(0);
      setTotalAllCount(0);
    }
  };

  const fetchKnownPlacements = async (itemId: number, isLegacy: boolean) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/get-charmdata?item_id=${itemId}&islegacy=${isLegacy}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setKnownPlacements(data.placements || []);
      } else {
        setKnownPlacements([]);
      }
    } catch (error) {
      console.error('Failed to fetch known placements:', error);
      setKnownPlacements([]);
    }
  };

  // Fetch weapon counts for dropdown
  const fetchWeaponCounts = async () => {
    try {
      // Single API call to get all weapon counts
      const response = await fetch(`${API_BASE_URL}/api/charmlinks/all-counts`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setWeaponCounts(data.counts);
      } else {
        console.error('Failed to fetch weapon counts');
      }
    } catch (error) {
      console.error('Failed to fetch weapon counts:', error);
    }
  };

  // Fetch charmlinks for selected weapon
  const fetchCharmlinks = async (defIndex: number, isLegacy: boolean) => {
    try {
      setFetchingCharmlinks(true); // Show loading state
      const resp = await fetch(`${API_BASE_URL}/api/charmlinks?def_index=${defIndex}&islegacy=${isLegacy}`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json();
      setNewPlacements(data.links || []);
      setImageReadyCount(data.imageReadyCount || 0);
      setTotalAllCount(data.totalAllCount || 0);
      
      // Store background data for alternative background
      if (data.background) {
        // You can store this in state if needed for the alternative background feature
        console.log('Background data:', data.background);
      }
      
      // Pre-fetch reference images for new placements
      if (data.links && data.links.length > 0) {
        preloadReferenceImages(data.links);
      }
    } catch (err) {
      setNewPlacements([]);
      setImageReadyCount(0);
      setTotalAllCount(0);
    } finally {
      setFetchingCharmlinks(false); // Hide loading state
    }
  };

  // Pre-fetch reference images in background
  const preloadReferenceImages = (placements: Charmlink[]) => {
    placements.forEach(placement => {
      if (placement.image) {
        const img = new Image();
        img.src = placement.image;
        // Don't need to do anything with the loaded image, just let it cache
      }
    });
  };

  // Accept optional inspectLink param for both button and placement click
  const fetchApiData = async (link?: string) => {
    const linkToUse = link || inspectLink;
    if (!linkToUse) return;
    setLoading(true);
    setError('');
    
    // Clear current charmlink and image when loading new inspect link
    setCurrentCharmlink(null);
    setReferenceImageUrl('https://screenshots.inspects.xyz/d8be940b-cab2-4e36-8089-5076cc814cbd-front.webp');
    
    try {
      const resp = await fetch(`${CS2INSPECTS_API_BASE_URL}/getGenCode?url=${encodeURIComponent(linkToUse)}`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json();
      const genCodeDetail = data.genCodeDetail || {};
      setApiData(genCodeDetail);
      
      // Set the background image URL for the grid
      const itemId = genCodeDetail.Item_ID;
      const skinId = genCodeDetail.Skin_ID;
      if (itemId && skinId) {
        setBackgroundImageUrl(`https://cdn.cs2inspects.com/customizer/${itemId}_${skinId}_0.webp`);
      } else {
        setBackgroundImageUrl('https://cdn.cs2inspects.com/customizer/7_44_0.webp');
      }
      
      // Auto-select weapon and legacy status based on the loaded item
      if (itemId) {
        const isLegacy = genCodeDetail.Item_Model_isLegacy;
        
        // Find the weapon name from the item_id
        let weaponName = '';
        for (const [name, id] of Object.entries(weapon_defindex_map)) {
          if (id === parseInt(itemId)) {
            weaponName = name;
            break;
          }
        }
        
        if (weaponName) {
          const legacyType = isLegacy ? 'legacy' : 'new';
          const weaponValue = `${weaponName}-${legacyType}`;
          setSelectedWeapon(weaponValue);
          
          // Fetch placements for this weapon+legacy combination
          fetchKnownPlacements(parseInt(itemId), isLegacy);
        }
      }
      
             // Try to find existing charmlink for this inspect link
       try {
         const charmlinkResp = await fetch(`${API_BASE_URL}/api/charmlinks/find-by-inspectlink?inspectlink=${encodeURIComponent(linkToUse)}`, {
           headers: getAuthHeaders(),
         });
         if (charmlinkResp.ok) {
           const charmlinkData = await charmlinkResp.json();
           if (charmlinkData.charmlink) {
             setCurrentCharmlink(charmlinkData.charmlink);
             
             // If charmlink exists but has no image, automatically generate one
             if (!charmlinkData.charmlink.image) {
               console.log('Charmlink found but no image, auto-generating...');
               setStatusMessage('Auto-generating image for this inspect link...');
               setStatusType('info');
               // Auto-generate image in background
               setTimeout(async () => {
                 try {
                   const charmlinkId = typeof charmlinkData.charmlink._id === 'string' 
                     ? charmlinkData.charmlink._id 
                     : charmlinkData.charmlink._id.$oid;
                   
                   if (charmlinkId) {
                     await generateImageForCharmlink(charmlinkId);
                     setStatusMessage('Image generated successfully!');
                     setStatusType('success');
                     setTimeout(() => setStatusMessage(''), 3000);
                   }
                 } catch (error) {
                   console.error('Auto-image generation failed:', error);
                   setStatusMessage('Auto-image generation failed');
                   setStatusType('error');
                   setTimeout(() => setStatusMessage(''), 5000);
                 }
               }, 1000); // Small delay to let UI settle
             } else {
               // Set reference image if available
               setReferenceImageUrl(charmlinkData.charmlink.image);
               setUseActualImage(true);
             }
           }
         }
       } catch (error) {
         console.log('Could not fetch charmlink data for manual inspect link');
       }
    } catch (err) {
      setError('Failed to fetch item data.');
      setApiData({});
      setReferenceImageUrl('https://screenshots.inspects.xyz/d8be940b-cab2-4e36-8089-5076cc814cbd-front.webp');
      setBackgroundImageUrl('https://cdn.cs2inspects.com/customizer/7_44_0.webp');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (isProcessing) {
      console.log('Save already in progress, ignoring click');
      return;
    }
    
    try {
      setIsProcessing(true);
      console.log('Save button clicked');
      console.log('apiData:', apiData);
      console.log('imagePosition:', imagePosition);
      console.log('currentPlacementId:', currentPlacementId);
      console.log('currentCharmlink:', currentCharmlink);
      
      const payload = {
        item_id: apiData.Item_ID,             // Weapon/item ID
        skin_id: apiData.Skin_ID,             // Skin ID
        charm_id: apiData.KeyChain_ID,        // Charm/Keychain ID
        islegacy: apiData.Item_Model_isLegacy,
        posid: "default-posid",               // Replace with your logic or a UI field
        posx: imagePosition.x,
        posy: imagePosition.y,
        x: apiData.KeyChain_X,
        y: apiData.KeyChain_Y,
        z: apiData.KeyChain_Z,                // Use KeyChain_Z from API
        isactive: true,
        itemdata: apiData,
        inspectlink: inspectLink,             // Add inspectlink to payload
        updated_by: currentUser,              // Track who made the change
        updated_at: new Date().toISOString(), // Track when the change was made
        charm_width: baseCharmWidth * charmScale,
        charm_height: baseCharmHeight * charmScale,
        charm_scale: charmScale,
        // Add objectid and image url from current charmlink if available
        charmlink_id: currentCharmlink?._id?.$oid || null,
        charmlink_image: currentCharmlink?.image || null,
      };
      
      console.log('Save payload:', payload);
      
      let res;
      if (currentPlacementId) {
        // Update existing
        console.log('Attempting to update placement with ID:', currentPlacementId);
        res = await fetch(`${API_BASE_URL}/api/update-charmdata/${currentPlacementId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });
      } else {
        // Insert new
        console.log('Creating new placement');
        res = await fetch(`${API_BASE_URL}/api/save-charmdata`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });
      }
      
      console.log('Save response status:', res.status);
      
      if (res.ok) {
        const responseData = await res.json();
        console.log('Save response data:', responseData);
        setStatusMessage(responseData.message || 'Saved successfully!');
        setStatusType('success');
        
        // If this was an update, set the new ID as current
        if (currentPlacementId && responseData.newId) {
          setCurrentPlacementId(responseData.newId);
        } else {
          setCurrentPlacementId(undefined);
        }
        
        // Clear current charmlink so next click will load a new placement
        setCurrentCharmlink(null);
        
        // Optionally refresh placements
        if (selectedWeapon) handleWeaponSelect({ target: { value: selectedWeapon } } as any);
        
        // Clear status message after 3 seconds
        setTimeout(() => setStatusMessage(''), 3000);
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('Save failed:', res.status, errorData);
        setStatusMessage(`Save failed! Status: ${res.status}, Error: ${errorData.error || 'Unknown error'}`);
        setStatusType('error');
        setTimeout(() => setStatusMessage(''), 5000);
      }
    } catch (error) {
      console.error('Save error:', error);
      setStatusMessage(`Save failed! Network error: ${error}`);
      setStatusType('error');
      setTimeout(() => setStatusMessage(''), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!currentPlacementId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/delete-charmdata/${currentPlacementId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          deleted_by: currentUser,
          deleted_at: new Date().toISOString(),
        }),
      });
      if (res.ok) {
        setStatusMessage('Deleted successfully!');
        setStatusType('success');
        setCurrentPlacementId(undefined);
        // Clear current charmlink so next click will load a new placement
        setCurrentCharmlink(null);
        if (selectedWeapon) handleWeaponSelect({ target: { value: selectedWeapon } } as any);
        setTimeout(() => setStatusMessage(''), 3000);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setStatusMessage(`Delete failed! Status: ${res.status}, Error: ${errorData.error || 'Unknown error'}`);
        setStatusType('error');
        setTimeout(() => setStatusMessage(''), 5000);
      }
    } catch (error) {
      console.error('Delete error:', error);
      setStatusMessage(`Delete failed! Network error: ${error}`);
      setStatusType('error');
      setTimeout(() => setStatusMessage(''), 5000);
    }
  };

  const handleLogout = () => {
    // Clear the JWT token
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
    setCurrentUser('');
    setAuthError('');
    // Clear any sensitive data
    setApiData({});
    setInspectLink('');
    setSelectedWeapon('');
    setKnownPlacements([]);
    setCurrentPlacementId(undefined);
    setStatusMessage('');
  };

  // Helper function to get auth headers for API requests
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  };

  // Charm base size constants (should match CanvasEditor)
  const baseCharmWidth = 100.37520114398815;
  const baseCharmHeight = 75.28140085799095;

  // Handler to load a charmlink (set inspect link, mark as loaded)
  const handleNewPlacementLoad = async (placement: Charmlink) => {
    setInspectLink(placement.inspectlink);
    setCurrentCharmlink(placement);
    
    // Clear current placement ID so we create a new placement instead of updating
    setCurrentPlacementId(undefined);
    
    // Set the reference image URL from the charmlink data
    if (placement.image) {
      setReferenceImageUrl(placement.image);
      // Set useActualImage to true by default for new placements with images
      setUseActualImage(true);
    }
    
    await fetchApiData(placement.inspectlink);
    
    // Reset zoom to 100% and center position
    setScale(1.0);
    setStagePosition({ x: 0, y: 0 });
    
    // Mark as loaded in backend
    const placementId = typeof placement._id === 'string' ? placement._id : placement._id.$oid;
    await fetch(`${API_BASE_URL}/api/charmlinks/${placementId}/loaded`, { 
      method: 'POST',
      headers: getAuthHeaders(),
    });
    // Refresh list and dots
    if (selectedWeapon) {
      const lastDash = selectedWeapon.lastIndexOf('-');
      const weaponName = selectedWeapon.substring(0, lastDash);
      const item_id = weapon_defindex_map[weaponName as keyof typeof weapon_defindex_map];
      if (item_id) {
        const isLegacy = selectedWeapon.endsWith('-legacy');
        fetchCharmlinks(item_id, isLegacy);
        // Refresh dots by re-fetching known placements
        fetchKnownPlacements(item_id, isLegacy);
      }
    }
  };

  // Handler to skip a charmlink
  const handleNewPlacementSkip = async (placement: Charmlink) => {
    console.log('=== handleNewPlacementSkip called ===');
    console.log('Placement received:', placement);
    
    if (!placement) {
      console.error('No placement provided to handleNewPlacementSkip');
      return;
    }
    
    // Handle both _id formats: { $oid: string } and plain string
    let placementId = null;
    if (typeof placement._id === 'string') {
      placementId = placement._id;
    } else if (placement._id && placement._id.$oid) {
      placementId = placement._id.$oid;
    }
    
    if (!placementId) {
      console.error('No valid _id found in placement:', placement);
      return;
    }
    
    try {
      console.log('Skipping placement:', placementId);
      console.log('ID type:', typeof placementId);
      console.log('ID length:', placementId.length);
      console.log('Full placement object:', placement);
      console.log('About to make API call to:', `${API_BASE_URL}/api/charmlinks/${placementId}/skip`);
      const skipResp = await fetch(`${API_BASE_URL}/api/charmlinks/${placementId}/skip`, { 
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (!skipResp.ok) {
        console.error('Skip failed:', skipResp.status, skipResp.statusText);
        const errorData = await skipResp.json().catch(() => ({}));
        console.error('Skip error details:', errorData);
        return;
      }
      
      console.log('Skip successful for placement:', placementId);
      
      // Refresh list and auto-load next
      if (selectedWeapon) {
        const lastDash = selectedWeapon.lastIndexOf('-');
        const weaponName = selectedWeapon.substring(0, lastDash);
        const item_id = weapon_defindex_map[weaponName as keyof typeof weapon_defindex_map];
        if (item_id) {
          const isLegacy = selectedWeapon.endsWith('-legacy');
          const resp = await fetch(`${API_BASE_URL}/api/charmlinks?def_index=${item_id}&islegacy=${isLegacy}`, {
            headers: getAuthHeaders(),
          });
          const data = await resp.json();
          setNewPlacements(data.links || []);
          setImageReadyCount(data.imageReadyCount || 0);
          setTotalAllCount(data.totalAllCount || 0);
          // Refresh dots by re-fetching known placements
          fetchKnownPlacements(item_id, isLegacy);
          // Auto-load next if available
          if (data.links && data.links.length > 0) {
            handleNewPlacementLoad(data.links[0]);
          } else {
            setCurrentCharmlink(null);
          }
        }
      }
    } catch (error) {
      console.error('Error skipping placement:', error);
    }
  };

  // Handler for Next button
  const handleNextNewPlacement = async () => {
    if (isProcessing) {
      console.log('Next already in progress, ignoring click');
      return;
    }
    
    try {
      setIsProcessing(true);
      console.log('Next button clicked');
      console.log('newPlacements length:', newPlacements?.length);
      console.log('currentCharmlink:', currentCharmlink);
      
      if (!newPlacements || newPlacements.length === 0) {
        console.log('No new placements available');
        return;
      }
      
      if (!currentCharmlink) {
        // If nothing loaded, load the first
        console.log('No current charmlink, loading first placement');
        handleNewPlacementLoad(newPlacements[0]);
        return;
      }
      
      // Since we only get one at a time, just fetch the next one
      if (selectedWeapon) {
        const lastDash = selectedWeapon.lastIndexOf('-');
        const weaponName = selectedWeapon.substring(0, lastDash);
        const item_id = weapon_defindex_map[weaponName as keyof typeof weapon_defindex_map];
        if (item_id) {
          const isLegacy = selectedWeapon.endsWith('-legacy');
          console.log('Fetching next charmlinks for weapon:', weaponName, 'legacy:', isLegacy);
          await fetchCharmlinks(item_id, isLegacy);
          setCurrentCharmlink(null);
        }
      }
    } catch (error) {
      console.error('Error in handleNextNewPlacement:', error);
      setStatusMessage('Next failed! Please try again.');
      setStatusType('error');
      setTimeout(() => setStatusMessage(''), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  // Add zoom handler
  const handleZoom = (delta: number) => {
    setScale(prev => {
      const newScale = Math.max(0.2, Math.min(5.0, prev + delta));
      setStagePosition({ x: 0, y: 0 }); // Reset pan on button zoom
      return newScale;
    });
  };

  // Reset zoom to normal (100%)
  const handleResetZoom = () => {
    setScale(1.0);
    setStagePosition({ x: 0, y: 0 }); // Reset pan position
  };

  // Save and then automatically load next placement
  const handleSaveAndNext = async () => {
    if (isProcessing) {
      console.log('Save+Next already in progress, ignoring click');
      return;
    }
    
    try {
      setIsProcessing(true);
      // First save the current placement
      await handleSave();
      
      // Clear current charmlink immediately after save so next will load properly
      setCurrentCharmlink(null);
      
      // Wait for fresh charmlinks to be fetched, then load next
      if (selectedWeapon) {
        const lastDash = selectedWeapon.lastIndexOf('-');
        const weaponName = selectedWeapon.substring(0, lastDash);
        const item_id = weapon_defindex_map[weaponName as keyof typeof weapon_defindex_map];
        if (item_id) {
          const isLegacy = selectedWeapon.endsWith('-legacy');
          
          // Fetch fresh charmlinks and then load the first one
          const resp = await fetch(`${API_BASE_URL}/api/charmlinks?def_index=${item_id}&islegacy=${isLegacy}`, {
            headers: getAuthHeaders(),
          });
          const data = await resp.json();
          setNewPlacements(data.links || []);
          setImageReadyCount(data.imageReadyCount || 0);
          setTotalAllCount(data.totalAllCount || 0);
          
          // Load the first available placement
          if (data.links && data.links.length > 0) {
            handleNewPlacementLoad(data.links[0]);
          }
        }
      }
    } catch (error) {
      console.error('Error in handleSaveAndNext:', error);
      setStatusMessage('Save+Next failed! Please try again.');
      setStatusType('error');
      setTimeout(() => setStatusMessage(''), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  // Toggle function to use actual image from charmlinks
  const handleActualImageToggle = () => {
    setUseActualImage(!useActualImage);
  };

  // Function to regenerate image
  const handleRegenImage = async () => {
    // Handle both _id formats: { $oid: string } and plain string
    let charmlinkId = null;
    if (typeof currentCharmlink?._id === 'string') {
      charmlinkId = currentCharmlink._id;
    } else if (currentCharmlink?._id?.$oid) {
      charmlinkId = currentCharmlink._id.$oid;
    }
    
    if (!charmlinkId) {
      console.error('No valid _id found in currentCharmlink:', currentCharmlink);
      return;
    }
    
    try {
      setImageGenerating(true); // Show loading state
      
      // Call our new regenerate-image endpoint
      const regenResp = await fetch(`${API_BASE_URL}/api/charmlinks/${charmlinkId}/regenerate-image`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (regenResp.ok) {
        const result = await regenResp.json();
        const newImageUrl = result.imageUrl;
        
        // Update the current charmlink with new image URL
        if (currentCharmlink) {
          setCurrentCharmlink({
            ...currentCharmlink,
            image: newImageUrl
          });
        }
        
        console.log('Image regenerated successfully:', newImageUrl);
        
        // Start polling for the image to be available
        pollForImage(newImageUrl);
      } else {
        const errorData = await regenResp.json();
        console.error('Failed to regenerate image:', errorData.error);
      }
    } catch (error) {
      console.error('Error regenerating image:', error);
    } finally {
      setImageGenerating(false); // Hide loading state
    }
  };

  // Function to generate image for a charmlink that doesn't have one
  const handleGenerateImage = async () => {
    console.log('=== handleGenerateImage called ===');
    console.log('currentCharmlink:', currentCharmlink);
    console.log('inspectLink:', inspectLink);
    
    // If we have a currentCharmlink with an _id, use that
    if (currentCharmlink?._id) {
      let charmlinkId = null;
      if (typeof currentCharmlink._id === 'string') {
        charmlinkId = currentCharmlink._id;
        console.log('Using string _id:', charmlinkId);
      } else if (currentCharmlink._id.$oid) {
        charmlinkId = currentCharmlink._id.$oid;
        console.log('Using $oid _id:', charmlinkId);
      }
      
      if (charmlinkId) {
        await generateImageForCharmlink(charmlinkId);
        return;
      }
    }
    
    // If no currentCharmlink or no _id, but we have an inspectLink, create a new charmlink first
    if (inspectLink && !currentCharmlink) {
      console.log('No currentCharmlink, creating new one for inspectLink:', inspectLink);
      await createCharmlinkAndGenerateImage();
      return;
    }
    
    console.error('Cannot generate image: no charmlink ID and no inspect link');
  };
  
  // Helper function to generate image for existing charmlink
  const generateImageForCharmlink = async (charmlinkId: string) => {
    try {
      setImageGenerating(true);
      console.log('Calling generate-image endpoint with ID:', charmlinkId);
      
      const genResp = await fetch(`${API_BASE_URL}/api/charmlinks/${charmlinkId}/generate-image`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (genResp.ok) {
        const result = await genResp.json();
        const newImageUrl = result.imageUrl;
        
        if (currentCharmlink) {
          setCurrentCharmlink({
            ...currentCharmlink,
            image: newImageUrl
          });
          
          // Also update the reference image URL to show the new image immediately
          setReferenceImageUrl(newImageUrl);
          
          // Set useActualImage to true to show the new generated image
          setUseActualImage(true);
        }
        
        console.log('Image generated successfully:', newImageUrl);
        pollForImage(newImageUrl);
      } else {
        const errorData = await genResp.json();
        console.error('Failed to generate image:', errorData.error);
      }
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setImageGenerating(false);
    }
  };
  
  // Helper function to create new charmlink and generate image
  const createCharmlinkAndGenerateImage = async () => {
    try {
      setImageGenerating(true);
      console.log('Creating new charmlink for inspectLink:', inspectLink);
      
      // First, create a new charmlink document
      const createResp = await fetch(`${API_BASE_URL}/api/charmlinks`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          inspectlink: inspectLink,
          def_index: apiData.Item_ID ? parseInt(apiData.Item_ID) : 0,
          islegacy: apiData.Item_Model_isLegacy || false
        })
      });
      
      if (createResp.ok) {
        const result = await createResp.json();
        const newCharmlink = result.charmlink;
        console.log('Created new charmlink:', newCharmlink);
        
        // Set as current charmlink
        setCurrentCharmlink(newCharmlink);
        
                 // Now generate the image
         if (newCharmlink._id) {
           const charmlinkId = typeof newCharmlink._id === 'string' ? newCharmlink._id : newCharmlink._id.$oid;
           await generateImageForCharmlink(charmlinkId);
           
           // After generating, also update the reference image URL
           if (newCharmlink.image) {
             setReferenceImageUrl(newCharmlink.image);
           }
         }
      } else {
        const errorData = await createResp.json();
        console.error('Failed to create charmlink:', errorData.error);
      }
    } catch (error) {
      console.error('Error creating charmlink:', error);
    } finally {
      setImageGenerating(false);
    }
  };

  // Function to poll for image availability
  const pollForImage = (imageUrl: string) => {
    let attempts = 0;
    const maxAttempts = 30; // 30 seconds max
    
    const checkImage = async () => {
      attempts++;
      
      try {
        const response = await fetch(imageUrl, { method: 'HEAD' });
        if (response.ok) {
          console.log('Image is now available:', imageUrl);
          // Force a re-render by updating the image URL and reference image
          if (currentCharmlink) {
            setCurrentCharmlink({
              ...currentCharmlink,
              image: imageUrl + '?t=' + Date.now() // Add timestamp to force reload
            });
          }
          // Also update the reference image URL to refresh the display
          setReferenceImageUrl(imageUrl + '?t=' + Date.now());
          return; // Stop polling
        }
      } catch (error) {
        console.log(`Attempt ${attempts}: Image not ready yet...`);
      }
      
      if (attempts < maxAttempts) {
        setTimeout(checkImage, 1000); // Check again in 1 second
      } else {
        console.log('Image polling timed out after 30 seconds');
      }
    };
    
    // Start polling after 1 second
    setTimeout(checkImage, 1000);
  };

  // Get the appropriate image URLs based on state
  const getReferenceImageUrl = () => {
    if (useActualImage) {
      // Check for current charmlink image first (for new placements)
      if (currentCharmlink?.image) {
        return currentCharmlink.image;
      }
      // Check for stored charmlink_image in existing placements
      if (referenceImageUrl && referenceImageUrl.includes('testing.inspects.xyz')) {
        return referenceImageUrl;
      }
    }
    return referenceImageUrl;
  };

  const getBackgroundImageUrl = () => {
    if (useActualImage) {
      // Check for current charmlink image first (for new placements)
      if (currentCharmlink?.image) {
        return currentCharmlink.image;
      }
      // Check for stored charmlink_image in existing placements
      if (referenceImageUrl && referenceImageUrl.includes('testing.inspects.xyz')) {
        return referenceImageUrl;
      }
    }
    return backgroundImageUrl;
  };

  // Force refresh of image display when currentCharmlink changes
  useEffect(() => {
    if (currentCharmlink?.image && useActualImage) {
      // Add timestamp to force browser to reload the image
      const freshImageUrl = currentCharmlink.image + '?t=' + Date.now();
      setReferenceImageUrl(freshImageUrl);
    }
  }, [currentCharmlink?.image, useActualImage]);


  return (
    <div className="App">
      {/* Authentication Modal */}
      <AuthModal
        isOpen={!isAuthenticated}
        onSteamLogin={handleSteamLogin}
        error={authError}
      />
      
      {isAuthenticated && (
        <>
          <header className="App-header">
            {/* Inspect Link Box with Title on Left */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}>
              {/* Left side - Charm Updater Title */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                width: '250px' // Match sidebar width
              }}>
                <h1 style={{ margin: 0, fontSize: '24px', color: 'var(--text-primary)' }}>Charm Updater</h1>
              </div>

              {/* Center - Inspect Link Box */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '8px',
                flex: 1,
                justifyContent: 'center'
              }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="text"
                    value={inspectLink}
                    onChange={handleInspectLinkChange}
                    placeholder="Paste inspect link here..."
                    style={{ 
                      width: '500px', 
                      maxWidth: '90%',
                      padding: '6px 36px 6px 6px', 
                      fontSize: 14,
                      borderRadius: '4px',
                      border: '1px solid var(--border-primary)',
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                  {inspectLink && (
                    <button
                      onClick={resetToHomeState}
                      style={{
                        position: 'absolute',
                        right: '4px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: '#ff4444',
                        cursor: 'pointer',
                        fontSize: '20px',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        transition: 'all 0.2s',
                        fontWeight: 'bold'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#ff4444';
                        e.currentTarget.style.color = 'white';
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#ff4444';
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                      }}
                      title="Clear and Reset to Home"
                    >
                      ×
                    </button>
                  )}
                </div>
                <button 
                  onClick={() => fetchApiData()} 
                  style={{ 
                    padding: '6px 16px', 
                    fontSize: 14,
                    borderRadius: '4px',
                    border: '1px solid var(--border-primary)',
                    backgroundColor: 'var(--accent-primary)',
                    color: 'var(--bg-primary)',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Load
                </button>
                {loading && <span style={{ color: 'var(--text-muted)', fontSize: 13, marginLeft: '8px' }}>Loading...</span>}
              </div>

              {/* Right side - Empty space to match right sidebar */}
              <div style={{ 
                width: '250px' // Match right sidebar width
              }}>
              </div>
            </div>
            
            {/* Status messages */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center',
              marginTop: '8px'
            }}>
              {error && <div style={{ color: 'var(--error)', fontSize: 13 }}>{error}</div>}
            </div>
          </header>
          <main className="App-main">
            <div style={{ display: 'flex', flexDirection: 'row', width: '100%', height: '100%' }}>
              {/* Left Sidebar */}
              <Sidebar
                weaponDefindexMap={weapon_defindex_map}
                selectedWeapon={selectedWeapon}
                onWeaponSelect={handleWeaponSelect}
                knownPlacements={knownPlacements}
                onPlacementClick={async (placement) => {
                  if (placement.inspectlink) {
                    setInspectLink(placement.inspectlink);
                    await fetchApiData(placement.inspectlink);
                    
                    // Use the stored image URL directly from the placement data
                    if (placement.charmlink_image) {
                      setReferenceImageUrl(placement.charmlink_image);
                      // Set useActualImage to true by default for existing placements with stored images
                      setUseActualImage(true);
                    } else {
                      // Fallback: Try to fetch the corresponding charmlink to get the actual image
                      try {
                        const charmlinkResp = await fetch(`${API_BASE_URL}/api/charmlinks/find-by-inspectlink?inspectlink=${encodeURIComponent(placement.inspectlink)}`, {
                          headers: getAuthHeaders(),
                        });
                        if (charmlinkResp.ok) {
                          const charmlinkData = await charmlinkResp.json();
                          if (charmlinkData.charmlink && charmlinkData.charmlink.image) {
                            setReferenceImageUrl(charmlinkData.charmlink.image);
                            setUseActualImage(true);
                          }
                        }
                      } catch (error) {
                        console.log('Could not fetch charmlink data for existing placement');
                      }
                    }
                  }
                  setImagePosition({ x: placement.posx, y: placement.posy });
                  const idValue = typeof placement._id === 'object' && placement._id?.$oid
                    ? placement._id.$oid
                    : typeof placement._id === 'string'
                      ? placement._id
                      : placement.id || 'No ID';
                  setCurrentPlacementId(idValue);
                }}
                currentPlacementId={currentPlacementId}
                newPlacements={newPlacements}
                imageReadyCount={imageReadyCount}
                totalAllCount={totalAllCount}
                onNewPlacementLoad={handleNewPlacementLoad}
                onNewPlacementSkip={handleNewPlacementSkip}
                weaponCounts={weaponCounts}
                fetchingCharmlinks={fetchingCharmlinks}
                currentUser={currentUser}
                onLogout={handleLogout}
              />

              {/* Main Column: Grid Canvas */}
              <div style={{ flex: '1 1 0%', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}>
                
                {/* Scrollable Content */}
                <div className="main-content-area" style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                  
                  {/* Grid/Canvas */}
                  {apiData.Item_ID && (
                    <>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'flex-start',
                        minHeight: '800px'
                      }}>
                        {/* Combined Controls Row - Charm Size, Zoom, Show Dots, and Image */}
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'row', 
                          alignItems: 'center',
                          gap: '12px',
                          padding: '2px 8px',
                          marginBottom: '8px',
                          marginLeft: '0',
                          position: 'relative',
                          zIndex: 1000,
                          backgroundColor: 'var(--bg-secondary)',
                          border: '1px solid var(--border-primary)',
                          borderRadius: '0px',
                          boxShadow: '0 1px 2px var(--shadow-light)'
                        }}>
                          {/* Charm Size Controls */}
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <label htmlFor="charm-scale-slider" style={{ 
                              fontWeight: 500, 
                              fontSize: '13px',
                              color: 'var(--text-secondary)',
                              minWidth: '60px'
                            }}>
                              Charm Size:
                            </label>
                            <input
                              id="charm-scale-slider"
                              type="range"
                              min={0.2}
                              max={5.0}
                              step={0.01}
                              value={charmScale}
                              onChange={e => setCharmScale(Number(e.target.value))}
                              style={{ 
                                width: '120px',
                                height: '18px'
                              }}
                            />
                            <span style={{ 
                              fontWeight: 600,
                              fontSize: '16px',
                              color: 'var(--accent-primary)',
                              width: '50px',
                              display: 'inline-block',
                              textAlign: 'left'
                            }}>
                              {charmScale.toFixed(2)}x
                            </span>
                            <span style={{ 
                              fontWeight: 500,
                              fontSize: '13px',
                              color: 'var(--text-secondary)',
                              marginLeft: '4px',
                              width: '80px',
                              display: 'inline-block',
                              textAlign: 'left'
                            }}>
                              ({Math.round(charmScale * 100)}x{Math.round(charmScale * 100)}px)
                            </span>
                          </div>
                          
                          {/* Divider */}
                          <div style={{
                            width: '1px',
                            height: '24px',
                            backgroundColor: 'var(--border-primary)',
                            margin: '0 2px'
                          }}></div>
                          
                          {/* Zoom Controls */}
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <label style={{ fontWeight: 500, fontSize: '14px', color: 'var(--text-secondary)', minWidth: '80px' }}>Zoom:</label>
                            <button onClick={() => handleZoom(-0.1)} style={{ fontSize: '18px', width: '32px', height: '32px' }}>-</button>
                            <span style={{ fontWeight: 600, fontSize: '16px', color: 'var(--accent-primary)', minWidth: '60px' }}>{(scale * 100).toFixed(0)}%</span>
                            <button onClick={() => handleZoom(0.1)} style={{ fontSize: '18px', width: '32px', height: '32px' }}>+</button>
                            <button 
                              onClick={handleResetZoom} 
                              style={{ 
                                fontSize: '12px', 
                                padding: '2px 4px',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '1px',
                                backgroundColor: 'var(--text-muted)',
                                color: 'white',
                                cursor: 'pointer',
                                fontWeight: '500',
                                marginLeft: '1px'
                              }}
                              title="Reset zoom to 100%"
                            >
                              Reset
                            </button>
                          </div>
                          
                          {/* Divider */}
                          <div style={{
                            width: '1px',
                            height: '24px',
                            backgroundColor: 'var(--border-primary)',
                            margin: '0 2px'
                          }}></div>
                          
                          {/* Show/Hide Dots Toggle */}
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <label style={{ fontWeight: 500, fontSize: '14px', color: 'var(--text-secondary)', minWidth: '80px' }}>Show Dots:</label>
                            <button 
                              onClick={() => setShowDots(!showDots)} 
                              style={{ 
                                fontSize: '13px', 
                                padding: '3px 8px',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '4px',
                                backgroundColor: showDots ? 'var(--success)' : 'var(--text-muted)',
                                color: 'white',
                                cursor: 'pointer',
                                fontWeight: '500'
                              }}
                            >
                              {showDots ? 'ON' : 'OFF'}
                            </button>
                          </div>
                          
                          {/* Divider */}
                          <div style={{
                            width: '1px',
                            height: '24px',
                            backgroundColor: 'var(--border-primary)',
                            margin: '0 2px'
                          }}></div>
                                                     
                          {/* Actual Image Toggle */}
                           <div style={{ 
                             display: 'flex', 
                             flexDirection: 'row', 
                             alignItems: 'center',
                             gap: '6px'
                           }}>
                             <label style={{ fontWeight: 500, fontSize: '14px', color: 'var(--text-secondary)', minWidth: '80px' }}>Image:</label>
                             <button 
                               onClick={handleActualImageToggle} 
                               style={{ 
                                 fontSize: '13px', 
                                 padding: '3px 8px',
                                 border: '1px solid var(--border-primary)',
                                 borderRadius: '4px',
                                 backgroundColor: useActualImage ? 'var(--warning)' : 'var(--text-muted)',
                                 color: useActualImage ? 'var(--bg-primary)' : 'white',
                                 cursor: 'pointer',
                                 fontWeight: '500'
                               }}
                               disabled={!currentCharmlink?.image}
                             >
                               {useActualImage ? 'ACTUAL' : 'NORMAL'}
                             </button>

                              

                                {currentCharmlink?.image ? (
                               <button 
                                 onClick={handleRegenImage} 
                                 style={{ 
                                   fontSize: '13px', 
                                   padding: '3px 8px',
                                   border: '1px solid var(--border-primary)',
                                   borderRadius: '4px',
                                   backgroundColor: 'var(--accent-primary)',
                                   color: 'var(--bg-primary)',
                                   cursor: 'pointer',
                                   fontWeight: '500'
                                 }}
                                 disabled={!currentCharmlink?.inspectlink || imageGenerating}
                                 title={`inspectlink: ${currentCharmlink?.inspectlink || 'none'}, imageGenerating: ${imageGenerating}`}
                               >
                                 {imageGenerating ? '🔄 GENERATING...' : 'REGEN'}
                               </button>
                             ) : (
                               <button 
                                 onClick={handleGenerateImage} 
                                 style={{ 
                                   fontSize: '13px', 
                                   padding: '3px 8px',
                                   border: '1px solid var(--border-primary)',
                                   borderRadius: '4px',
                                   backgroundColor: 'var(--success)',
                                   color: 'white',
                                   cursor: 'pointer',
                                   fontWeight: '500'
                                 }}
                                 disabled={!inspectLink || imageGenerating}
                                 title={`inspectlink: ${inspectLink || 'none'}, imageGenerating: ${imageGenerating}`}
                               >
                                 {imageGenerating ? '🔄 GENERATING...' : 'GENERATE IMAGE'}
                               </button>
                             )}
                          </div>
                        </div>
                        {/* Scrollable grid wrapper */}
                        <div className="grid-scroll-wrapper" style={{
                          width: '100%',
                          overflowX: 'auto',
                          overflowY: 'auto',
                          maxWidth: '100%',
                          position: 'relative'
                        }}>
                          {/* Loading overlay when generating image */}
                          {imageGenerating && (
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              zIndex: 1000,
                              color: 'white',
                              fontSize: '18px'
                            }}>
                              <div style={{ marginBottom: '20px' }}></div>
                              <div>Generating image...</div>
                              <div style={{ fontSize: '14px', marginTop: '10px', opacity: 0.8 }}>
                                This may take a few seconds
                              </div>
                            </div>
                          )}
                          <div className="reference-canvas-wrapper" 
                            style={{ 
                              display: 'flex', 
                              justifyContent: 'flex-start',
                              alignItems: 'flex-start',
                              paddingTop: '0px',
                              paddingLeft: '0px',
                              marginTop: '-50px',
                              marginLeft: '-100px',
                              width: `${1920 * scale}px`,
                              height: `${1080 * scale}px`,
                              cursor: 'zoom-in'
                            }}
                          >
                            <CanvasEditor 
                              width={1920}
                              height={1080}
                              imagePosition={imagePosition}
                              onPositionChange={setImagePosition}
                              overlayImageUrl={apiData.KeyChain_Image_Front}
                              backgroundImageUrl={getBackgroundImageUrl()}
                              existingPlacements={knownPlacements}
                              charmScale={charmScale}
                              scale={scale}
                              stagePosition={stagePosition}
                              onScaleChange={setScale}
                              onStagePositionChange={setStagePosition}
                              showDots={showDots}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Fixed Bottom Action Bar */}
              <div style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'var(--bg-secondary)',
                borderTop: '1px solid var(--border-primary)',
                padding: '12px 20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                boxShadow: '0 -2px 10px var(--shadow)',
                zIndex: 1000
              }}>
                {/* Center - Action buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center'
                }}>
                  <button
                    onClick={() => {
                      console.log('Skip button clicked');
                      console.log('currentCharmlink:', currentCharmlink);
                      console.log('Button disabled state:', !currentCharmlink || currentCharmlink?.isskip);
                      if (currentCharmlink) {
                        console.log('Calling handleNewPlacementSkip');
                        handleNewPlacementSkip(currentCharmlink);
                      } else {
                        console.log('No currentCharmlink, cannot skip');
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid var(--text-muted)',
                      backgroundColor: 'var(--text-muted)',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    disabled={!currentCharmlink || currentCharmlink?.isskip}
                  >
                    {currentCharmlink && currentCharmlink.isskip ? 'Skipped' : 'Skip'}
                  </button>
                  <button
                    onClick={handleNextNewPlacement}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid var(--accent-primary)',
                      backgroundColor: 'var(--accent-primary)',
                      color: 'var(--bg-primary)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    disabled={newPlacements.length === 0 || isProcessing}
                  >
                    Next
                  </button>
                  <button
                    onClick={handleSave}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid var(--success)',
                      backgroundColor: 'var(--success)',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    disabled={isProcessing}
                  >
                    Save
                  </button>
                  <button
                    onClick={handleSaveAndNext}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid var(--warning)',
                      backgroundColor: 'var(--warning)',
                      color: 'var(--bg-primary)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    disabled={newPlacements.length === 0 || isProcessing}
                  >
                    Save+Next
                  </button>
                  <button
                    onClick={handleDelete}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid var(--error)',
                      backgroundColor: 'var(--error)',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    disabled={!currentPlacementId}
                  >
                    Delete
                  </button>
                </div>
                
                {/* Right side - Status Message */}
                {statusMessage && (
                  <div style={{
                    position: 'absolute',
                    right: '20px',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    backgroundColor: statusType === 'success' ? 'rgba(76, 175, 80, 0.2)' : statusType === 'error' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(102, 192, 244, 0.2)',
                    color: statusType === 'success' ? 'var(--success)' : statusType === 'error' ? 'var(--error)' : 'var(--accent-primary)',
                    border: `1px solid ${statusType === 'success' ? 'var(--success)' : statusType === 'error' ? 'var(--error)' : 'var(--accent-primary)'}`,
                    maxWidth: '300px',
                    wordWrap: 'break-word'
                  }}>
                    {statusMessage}
                  </div>
                )}
              </div>

              {/* Right Sidebar - Hidden */}
              {/* {currentUser === 'admin' && (
                <RightSidebar
                  apiData={apiData}
                  imagePosition={imagePosition}
                  currentPlacementId={currentPlacementId}
                />
              )} */}
            </div>
          </main>
        </>
      )}
    </div>
  );
}

export default App;
